import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';

export interface DealFilters {
  pipelineId?: string;
  stageId?: string;
  status?: string;
  organizationId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface Deal {
  id: string;
  name: string;
  pipelineId: string;
  stageId: string;
  stageName?: string;
  stageColor?: string;
  status: 'open' | 'won' | 'lost';
  dealType?: string;
  premium?: number;
  commission?: number;
  carrier?: string;
  coverageTypes?: string[];
  policyNumber?: string;
  effectiveDate?: string;
  expirationDate?: string;
  organizationId?: string;
  organizationName?: string;
  contactIds?: string[];
  contacts?: { id: string; firstName: string; lastName: string; email?: string }[];
  ownerId?: string;
  ownerName?: string;
  tags?: string[];
  notes?: string;
  customFields?: Record<string, string>;
  stageEnteredAt?: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DealsResponse {
  deals: Deal[];
  total: number;
  limit: number;
  offset: number;
}

function buildQueryString(filters?: DealFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.pipelineId) params.set('pipelineId', filters.pipelineId);
  if (filters.stageId) params.set('stageId', filters.stageId);
  if (filters.status) params.set('status', filters.status);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useDeals(filters?: DealFilters) {
  return useQuery({
    queryKey: ['deals', filters],
    queryFn: () => fetchJson<DealsResponse>(`/api/deals${buildQueryString(filters)}`),
  });
}

export function useDeal(id: string | null) {
  return useQuery({
    queryKey: ['deal', id],
    queryFn: () => fetchJson<Deal>(`/api/deals/${id}`),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Deal>) => postJson<Deal>('/api/deals', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Deal> }) =>
      putJson<Deal>(`/api/deals/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deal', variables.id] });
    },
  });
}

export function useUpdateDealStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) =>
      putJson<Deal>(`/api/deals/${id}/stage`, { stageId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['deal', variables.id] });
    },
  });
}
